/* eslint-disable no-nested-ternary */
import { useEffect } from 'react';
import { TreeView, TreeItem } from '@mui/lab';
import { Box, Typography } from '@mui/material';
import {
  ChevronRight, ExpandMore,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import InsertChartOutlinedOutlinedIcon from '@mui/icons-material/InsertChartOutlinedOutlined';

function ViewerNode({ node, handleApiCall, selectedRadioId }) {
  return (
    <TreeItem
      nodeId={node.id}
      selectionDisabled
      label={(
        <Box
          sx={{
            py: 0.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            ...(selectedRadioId === node.id
              ? {
                backgroundColor: 'primary.lighter',
                color: 'primary.main',
              }
              : {
                ':hover': {
                  backgroundColor: 'grey.100',
                },
              }),
            borderRadius: '6px',
            px: 2,
          }}
          onClick={() => handleApiCall(node.id)}
        >
          <Typography>{node.name}</Typography>
        </Box>
      )}
      sx={{
        '& .MuiTreeItem-content': {
          borderRadius: '8px',
          width: 'max-content',
          my: 0.2,
          gap: 1,
        },
        '& .MuiTreeItem-content:hover': {
          backgroundColor: 'transparent !important',
        },
        '& .MuiTreeItem-content.Mui-selected': {
          backgroundColor: 'transparent !important',
        },
        '& .MuiTreeItem-content.Mui-focused': {
          backgroundColor: 'transparent !important',
        },
        '& .MuiTreeItem-label': {
          pr: 2,
        },
        '& .MuiTreeItem-group': {
          ml: 2,
          pl: 1,
          borderLeft: '2px dashed #E0E0E0',
        },
      }}
    >
      {node.features?.length > 0 ? (
        node.features.map((f) => (
          <TreeItem
            key={f.id}
            nodeId={`${node.id}-${f.id}`}
            label={(
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1,
                  maxWidth: { sm: '100%', md: 220, xl: '100%' },
                  my: 0.5,
                }}
              >
                <InsertChartOutlinedOutlinedIcon fontSize="small" />
                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    lineHeight: 1.4,
                    overflowWrap: 'anywhere',
                  }}
                >
                  {f.featureName}
                </Typography>
              </Box>
            )}
            sx={{
              pointerEvents: 'none',
              '& .MuiTreeItem-label': {
                color: 'grey.600',
                pr: 2,
              },
            }}
          />
        ))
      ) : node.children?.length > 0 ? (
        node.children.map((child) => (
          <ViewerNode
            key={child.id}
            node={child}
            handleApiCall={handleApiCall}
            selectedRadioId={selectedRadioId}
          />
        ))
      ) : null}
    </TreeItem>
  );
}

function TaxonomyViewer({
  selectedView, selectedRadioId, expanded, setExpanded, setSelectedRadioId, handleApiCall,
}) {
  const taxonomyDetails = useSelector((state) => state.taxonomyDetails?.data);

  useEffect(() => {
    setExpanded([]);
    setSelectedRadioId(null);
  }, [selectedView]);

  return (
    <TreeView
      defaultCollapseIcon={<ExpandMore />}
      defaultExpandIcon={<ChevronRight />}
      expanded={expanded}
      onNodeToggle={(event, nodeIds) => setExpanded(nodeIds)}
      sx={{
        minHeight: '400px',
        height: 'calc(100% - 50px)',
        overflowY: 'hidden',
        overflowX: 'hidden',
        mt: 2,

        '&:hover': {
          overflowY: 'auto',
          overflowX: 'auto',
        },

        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'primary.main',
          borderRadius: 4,
        },
      }}
    >
      {taxonomyDetails?.tree?.map((tree, i) => (
        <ViewerNode
          key={`tree-${i}`}
          node={tree}
          handleApiCall={handleApiCall}
          selectedRadioId={selectedRadioId}
        />
      ))}
    </TreeView>
  );
}

export default TaxonomyViewer;
